/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import Link from "next/link";
import { CSSProperties, useState } from "react";

import classes from "./StoreDetails.module.css";

import { DirectionsMap } from "@/components/Map";
import { Popover, Text } from "@mantine/core";

import {
	CopyOutlineIcon,
	InfoOutlineIcon,
	PhoneOutlineIcon,
} from "@/components/Icons";
import Button from "@/components/buttons/Button";
import { toastMe } from "@/utils";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { FreeMode, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

interface Props {
	className?: string;
	style?: CSSProperties;

	store: StoreItem;
}

const getFullSizeMapEmbed = (mapEmbed?: string | null) => {
	if (!mapEmbed) return null;

	const withDimensions = mapEmbed.replace(
		/<iframe/i,
		'<iframe width="100%" height="100%"'
	);

	if (/style="/i.test(withDimensions)) {
		return withDimensions.replace(/style="([^"]*)"/i, (_m, style) => {
			const normalized = style
				.replace(/\bwidth\s*:[^;]*;?/gi, "")
				.replace(/\bheight\s*:[^;]*;?/gi, "")
				.trim();
			return `style="${normalized}${normalized ? "; " : ""}width:100%; height:100%;"`;
		});
	}

	return withDimensions.replace(
		/<iframe/i,
		'<iframe style="width:100%; height:100%;"'
	);
};

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const StoreDetails = ({
	className,
	style,

	store,
}: Props) => {
	const [activeImageIndex, setActiveImageIndex] = useState(
		// store?.media?.length && store?.media?.length > 2
		// 	? Math.ceil(store.media.length / 2) - 1
		// 	: 0
		0
	);
	// const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>();
	// const [clientLocation, setClientLocation] = useState<[number, number]>([0, 0]);
	const centered =
		store.media?.length && store.media?.length > 2 ? true : false;

	const classNames = [classes.StoreDetails, className];

	const times = store.openTime.split("-");
	const s: string[] = [];
	const e: string[] = [];
	let closed: string;
	times.forEach((time, i) => {
		if (time.match(/closed/i)) {
			closed = time;
		}

		time.split(" ").forEach((t, j) => {
			if (s.length < 2) {
				if (t.includes(":") || t.includes("AM") || t.includes("PM")) {
					s.push(t);
				}
			} else if (e.length < 2) {
				if (t.includes(":") || t.includes("AM") || t.includes("PM")) {
					e.push(t);
				}
			}
		});
	});

	const startTime = s.join(" ");
	const endTime = e.join(" ");

	// Sun: 7:00 AM - 10:00 PM
	const openTimes: string[] = [];

	days.forEach((day, i) => {
		if (!closed?.includes(day)) {
			const time = day + ": " + startTime + " - " + endTime;
			openTimes.push(time);
		}
	});

	const shareText = `${store.name}\n${store.address}\n${store.phone}\n${
		store.openTime
	}}${store.closeDay ? " - " + store.closeDay + " Closed" : ""}`;
	const mapEmbedHtml = getFullSizeMapEmbed(
		(store as StoreItem & { mapEmbed?: string | null })?.mapEmbed
	);

	const handleOpenDirections = () => {
		if (typeof window === "undefined") return;

		const destination = `${store?.lat},${store?.long}`;
		const openMaps = (origin?: string) => {
			const params = new URLSearchParams({
				api: "1",
				destination,
			});

			if (origin) {
				params.set("origin", origin);
			}

			window.open(
				`https://www.google.com/maps/dir/?${params.toString()}`,
				"_blank",
				"noopener,noreferrer"
			);
		};

		if (!navigator.geolocation) {
			openMaps();
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				openMaps(
					`${position.coords.latitude},${position.coords.longitude}`
				);
			},
			() => {
				openMaps();
			},
			{
				enableHighAccuracy: true,
				timeout: 8000,
				maximumAge: 60000,
			}
		);
	};

	return (
		<div className={classNames.join(" ")} style={style}>
			<div className={`${classes.container} container`}>
				<div className={classes.body}>
					<div className={classes.header}>
						<div className="flex gap-2 mb-2">
							<h2 className={classes.title}>{store.name}</h2>

							<button
								onClick={() => {
									navigator.clipboard.writeText(shareText);
									toastMe("Copied to clipboard", "info");
								}}
								className="mb-1">
								<CopyOutlineIcon
									width={18}
									height={18}
									className={""}
								/>
							</button>

							{store.phone && (
								<Link
									className={classes.callNow}
									href={`tel:${store.phone}`}>
									<PhoneOutlineIcon
										className={classes.icon}
									/>
									<p className={classes.text}>Call Now</p>
									{/* <p className={classes.value + ' underline'}>{store.phone}</p> */}
								</Link>
							)}
						</div>

						<div className={classes.row + " " + classes.xl}>
							<p className={classes.subtitle}>{store.address}</p>
							{store.openTime && (
								<span className="hidden xl:inline-block">
									|
								</span>
							)}
							{store.openTime && (
								<div className={classes.row}>
									<p className={classes.text}>
										Opening Hours:
									</p>
									<p className={classes.subtitle}>
										{store.openTime}
									</p>
									<Popover
										width={200}
										position="bottom-end"
										withArrow
										shadow="md">
										<Popover.Target>
											<button>
												<InfoOutlineIcon className="mb-1" />
											</button>
										</Popover.Target>
										<Popover.Dropdown>
											{openTimes.map((time, i) => (
												<Text
													key={i}
													size="xs"
													className="mt-1.5">
													{time}
												</Text>
											))}
										</Popover.Dropdown>
									</Popover>
								</div>
							)}
						</div>
					</div>

					<div className={classes.description}>
						<Button
							type="button"
							onClick={handleOpenDirections}
							className={classes.button}>
							Direction on Map
						</Button>
					</div>
				</div>
			</div>

			<div className={classes.contents}>
				{/* <div className={classes.left}>
                </div> */}

				<div className={`${classes.container} container`}>
					{!!store.media?.length && (
						<div
							className={classes.mainImage}
							key={activeImageIndex}>
							{/* <Image
                                src={store.images?.[activeImageIndex]}
                                alt={store.label}
                                layout="responsive"
                                width={800}
                                height={800}
                                style={{
                                    aspectRatio: 1,
                                    objectFit: 'cover',
                                }}
                                className={classes.image}
                                unoptimized={true}
                            /> */}
							<Swiper
								key={activeImageIndex}
								navigation
								initialSlide={activeImageIndex}
								spaceBetween={8}
								slidesPerView={1}
								slidesPerGroup={1}
								
								// centeredSlides={centered}
								freeMode={false}
								modules={[Navigation, FreeMode]}
								className={classes.swiper}
								// onSlideChangeTransitionEnd={swiper => {
								//     setActiveImageIndex(swiper.activeIndex);
								// }}
								// thumbs={thumbsSwiper ? { swiper: thumbsSwiper } : {}}
							>
								{store?.media?.map((image, i) => (
									<SwiperSlide
										key={i}
										className=" h-auto inline-block lg:flex items-center justify-center w-auto aspect-auto"
									>
										<img
											src={image}
											alt="image"
											className="w-full h-full aspect-auto"
											style={{
												// aspectRatio: 1,
												// objectFit: "contain",
												height: "100%",
												width: "100%",
											}}
										/>
									</SwiperSlide>
								))}
							</Swiper>
						</div>
					)}
				</div>

				<div className={`${classes.container} container`}>
					{!!store.media?.length && store.media?.length > 0 && (
						<div className={classes.changer}>
							<Swiper
				
								// initialSlide={activeImageIndex}
								spaceBetween={16}
								slidesPerView={4}
								// loopedSlides={store.media.length}
								navigation={store.media.length > 4}
								// loop={store.media.length > 4}
								// centeredSlides={store.media.length > 4}
								
								modules={[Navigation]}
								className={classes.swiper}
								allowTouchMove={store.media.length > 4}>
								{store.media?.map((image, i) => (
									<SwiperSlide key={i}>
										<Image
											src={image}
											alt={store.name?? 'store image'}
											layout="responsive"
											width={1500}
											height={1000}
											style={{
												aspectRatio: 1500 / 1000,
												objectFit: "cover",
												cursor: "pointer",
											}}
											className={`${classes.image} ${
												activeImageIndex === i &&
												classes.active
											}`}
											onClick={() =>
												setActiveImageIndex(i)
											}
											unoptimized={true}
										/>
									</SwiperSlide>
								))}
								{store.media.length < 4 && Array.from({ length: 4 - store.media.length }).map((_, i) => (
									<SwiperSlide key={`placeholder-${i}`}>
										<div
											style={{
												aspectRatio: 1500 / 1000,
												backgroundColor: "#f5f5f5",
												borderRadius: "8px",
												border: "1px dashed #d0d0d0",
											}}
										/>
									</SwiperSlide>
								))}
							</Swiper>
						</div>
					)}

					<div className={classes.right}>
						{/* <GoogleMap
                            streetAddress={store.location}
                            setClientLocation={setClientLocation}
                            cords={[
                                store.cords[0],
                                store.cords[1]
                            ]}
                        /> */}

						{mapEmbedHtml ? (
							<div
								className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full"
								dangerouslySetInnerHTML={{ __html: mapEmbedHtml }}
							/>
						) : (
							<DirectionsMap cords={[store.lat, store.long]} />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default StoreDetails;
